import { Box, CircularProgress, Stack } from '@mui/material';
import { BenefitDTOBenefitEnum } from 'probonio-shared-ui/api';
import { apis, useTenantQuery } from 'probonio-shared-ui/module/api';
import { useWithMessage } from 'probonio-shared-ui/module/error';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { BenefitPaper } from '../../component/benefitTabs/BenefitPaper';
import { FullWidthCenterBox } from '../../component/layout/BoxLayout';
import { getBenefitIcon } from '../../module/benefits/BenefitIcon';
import BenefitSubPage from '../../module/benefits/BenefitSubPage';
import { BikeleasingContractDetails } from '../../module/benefits/bikeleasingV2/contracts/BikeleasingContractDetails';
import { useBikeleasingPrecondition } from '../../module/benefits/bikeleasingV2/useBikeleasingPrecondition';
import { AuthorizedLayout } from '../../module/navigation/AuthorizedLayout';
import { LetterAvatar } from '../../module/userManagement/LetterAvatar';
import { renderUserName } from '../../module/userManagement/userFormatter';
import { useNavigateBack } from '../../util/useNavigateBack';

const BikeleasingContractDetailsPage: React.FC = () => {
  const { t } = useTranslation('benefitsModule');
  const { PreconditionComponent, isPreconditionMet } = useBikeleasingPrecondition();
  const { contractId } = useParams();
  const withMessage = useWithMessage();
  const benefit = BenefitDTOBenefitEnum.BikeleasingV2.toLowerCase();
  const backNavigator = useNavigateBack(`/benefits/${benefit}/contracts`);

  const handleError = useCallback(
    (error: Error) => {
      withMessage(error);
      backNavigator();
    },
    [backNavigator, withMessage],
  );

  const { data: contract, isLoading: isContractLoading } = useTenantQuery(
    ['benefit', 'bikeleasing', 'contracts', contractId],
    tenantId =>
      apis.bikeLeasingV2
        .getContractById({
          tenantId,
          contractId: contractId!,
        })
        .then(res => res.data)
        .catch(e => {
          handleError(e);
          return null;
        }),
    { enabled: isPreconditionMet && !!contractId },
  );

  return (
    <AuthorizedLayout
      title={contract?.employee ? renderUserName(contract.employee, contract?.employee.personalNumber) : contract?.contractNumber}
      breadcrumbs={[
        { title: t('extensions:bikeleasing_v2.title'), to: `/benefits/${benefit}` },
        { title: t('extensions:bikeleasing_v2_benefit.contracts'), to: `/benefits/${benefit}/contracts` },
      ]}
    >
      <BenefitSubPage
        icon={getBenefitIcon(BenefitDTOBenefitEnum.BikeleasingV2)}
        title={t('extensions:bikeleasing_v2.contract.contractDetailsHeadline')}
        subTitle={
          isPreconditionMet &&
          contract?.employee && (
            <Stack mt={0.5} direction="row">
              <LetterAvatar size={30} firstName={contract?.employee?.firstName} lastName={contract?.employee?.lastName} />
              <Box alignSelf="center" ml={1}>
                {renderUserName(contract.employee, contract.employee.personalNumber)}
              </Box>
            </Stack>
          )
        }
      >
        <BenefitPaper>
          {!isPreconditionMet && PreconditionComponent}
          {isPreconditionMet && isContractLoading && (
            <FullWidthCenterBox>
              <CircularProgress />
            </FullWidthCenterBox>
          )}
          {isPreconditionMet && contract && <BikeleasingContractDetails contract={contract} />}
        </BenefitPaper>
      </BenefitSubPage>
    </AuthorizedLayout>
  );
};

export default BikeleasingContractDetailsPage;
